import React, { useState, useEffect } from 'react';
import { Gallery } from '../../types/Gallery';
import { BlogPost } from '../../types/Blog';
import SitemapGenerator from './SitemapGenerator';
import BacklinkTracker from './BacklinkTracker';
import SEOSettingsForm from './SEOSettingsForm';
import PageSEOEditor from './PageSEOEditor';
import SEOHealthChecker from './SEOHealthChecker';
import BulkSEOTools from './BulkSEOTools';
import SEOQuickActions from './SEOQuickActions';
import HelpButton from '../Admin/HelpButton';

interface SEODashboardProps {
  galleries: Gallery[];
  blogPosts: BlogPost[];
  baseUrl: string;
  onRefresh?: () => void;
}

interface SEOStats {
  totalPages: number;
  totalImages: number;
  totalBlogPosts: number;
  avgImagesPerGallery: number;
  seoScore: number;
  missingAltTags: number;
  missingDescriptions: number;
  lastUpdated: string;
}

const SEODashboard: React.FC<SEODashboardProps> = ({ galleries, blogPosts, baseUrl, onRefresh }) => {
  const [stats, setStats] = useState<SEOStats>({
    totalPages: 0,
    totalImages: 0,
    totalBlogPosts: 0,
    avgImagesPerGallery: 0,
    seoScore: 0,
    missingAltTags: 0,
    missingDescriptions: 0,
    lastUpdated: 'Never'
  });
  const [activeTab, setActiveTab] = useState<'overview' | 'pages' | 'content' | 'sitemap' | 'health' | 'settings'>('overview');

  useEffect(() => {
    console.log('SEO Dashboard - Galleries:', galleries.length);
    console.log('SEO Dashboard - Blog Posts:', blogPosts.length);
    console.log('SEO Dashboard - Blog Posts Data:', blogPosts);
    calculateSEOStats();
  }, [galleries, blogPosts]);

  const calculateSEOStats = () => {
    const totalImages = galleries.reduce((total, gallery) => total + (gallery.images?.length || 0), 0);
    const avgImagesPerGallery = galleries.length > 0 ? totalImages / galleries.length : 0;
    
    // Calculate missing alt tags (simplified - in real implementation, you'd check each image)
    const missingAltTags = galleries.reduce((total, gallery) => {
      return total + (gallery.images?.filter(img => !img.alt || img.alt.trim() === '').length || 0);
    }, 0);

    // Calculate missing descriptions
    const missingDescriptions = galleries.filter(gallery => !gallery.description || gallery.description.trim() === '').length;

    // Calculate SEO score (simplified)
    let seoScore = 100;
    seoScore -= (missingAltTags / totalImages) * 30; // Penalty for missing alt tags
    seoScore -= (missingDescriptions / galleries.length) * 20; // Penalty for missing descriptions
    seoScore -= (blogPosts.length < 5) ? 10 : 0; // Penalty for low blog content
    seoScore = Math.max(0, Math.min(100, seoScore));

    setStats({
      totalPages: 5 + galleries.length + blogPosts.length, // Main pages + galleries + blog posts
      totalImages,
      totalBlogPosts: blogPosts.length,
      avgImagesPerGallery: Math.round(avgImagesPerGallery * 10) / 10,
      seoScore: Math.round(seoScore),
      missingAltTags,
      missingDescriptions,
      lastUpdated: new Date().toLocaleString()
    });
  };

  const getSEOScoreColor = (score: number) => {
    if (score >= 80) return '#16a34a';
    if (score >= 60) return '#f59e0b';
    return '#dc2626';
  };

  const getSEOScoreLabel = (score: number) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Needs Improvement';
    return 'Poor';
  };

  return (
    <div className="seo-dashboard">
      <div className="seo-header">
        <div className="seo-header-content">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div>
              <h2>SEO Dashboard</h2>
              <p>Monitor and improve your website's search engine optimization</p>
            </div>
            <HelpButton section="seo" tooltip="SEO Dashboard Help" />
          </div>
          {onRefresh && (
            <button 
              className="btn btn-secondary"
              onClick={onRefresh}
              title="Refresh data"
            >
              <i className="fas fa-sync-alt"></i> Refresh
            </button>
          )}
        </div>
        <div className="seo-tabs">
          <button 
            className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <i className="fas fa-chart-line"></i> Overview
          </button>
          <button 
            className={`tab-btn ${activeTab === 'pages' ? 'active' : ''}`}
            onClick={() => setActiveTab('pages')}
          >
            <i className="fas fa-file-alt"></i> Pages
          </button>
          <button 
            className={`tab-btn ${activeTab === 'content' ? 'active' : ''}`}
            onClick={() => setActiveTab('content')}
          >
            <i className="fas fa-images"></i> Content
          </button>
          <button 
            className={`tab-btn ${activeTab === 'sitemap' ? 'active' : ''}`}
            onClick={() => setActiveTab('sitemap')}
          >
            <i className="fas fa-sitemap"></i> Sitemap
          </button>
          <button 
            className={`tab-btn ${activeTab === 'health' ? 'active' : ''}`}
            onClick={() => setActiveTab('health')}
          >
            <i className="fas fa-heartbeat"></i> Health Check
          </button>
          <button 
            className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            <i className="fas fa-sliders-h"></i> Settings
          </button>
        </div>
      </div>

      {activeTab === 'overview' && (
        <div className="seo-overview">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem' }}>
            <h3>SEO Overview</h3>
            <HelpButton section="seo" tooltip="SEO Overview Help" />
          </div>
          {/* SEO Score */}
          <div className="seo-score-card">
            <div className="score-main">
              <div className="score-circle">
                <div 
                  className="score-value"
                  style={{ color: getSEOScoreColor(stats.seoScore) }}
                >
                  {stats.seoScore}
                </div>
                <div className="score-label">SEO Score</div>
              </div>
              <div className="score-details">
                <h3>{getSEOScoreLabel(stats.seoScore)}</h3>
                <p>Your website's overall SEO performance</p>
                <div className="score-breakdown">
                  <div className="breakdown-item">
                    <span>Content Quality</span>
                    <span className="breakdown-score">85%</span>
                  </div>
                  <div className="breakdown-item">
                    <span>Technical SEO</span>
                    <span className="breakdown-score">78%</span>
                  </div>
                  <div className="breakdown-item">
                    <span>Image Optimization</span>
                    <span className="breakdown-score">92%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="seo-stats-grid">
            <div className="stat-card">
              <div className="stat-icon">
                <i className="fas fa-file-alt"></i>
              </div>
              <div className="stat-content">
                <h3>{stats.totalPages}</h3>
                <p>Total Pages</p>
                <div className="stat-change positive">
                  <i className="fas fa-arrow-up"></i> +{galleries.length + blogPosts.length} this month
                </div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <i className="fas fa-images"></i>
              </div>
              <div className="stat-content">
                <h3>{stats.totalImages}</h3>
                <p>Total Images</p>
                <div className="stat-change neutral">
                  <i className="fas fa-chart-line"></i> {stats.avgImagesPerGallery} avg per gallery
                </div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <i className="fas fa-blog"></i>
              </div>
              <div className="stat-content">
                <h3>{stats.totalBlogPosts}</h3>
                <p>Blog Posts</p>
                <div className="stat-change positive">
                  <i className="fas fa-plus"></i> Great for SEO
                </div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <div className="stat-content">
                <h3>{stats.missingAltTags}</h3>
                <p>Missing Alt Tags</p>
                <div className="stat-change negative">
                  <i className="fas fa-arrow-down"></i> Needs attention
                </div>
              </div>
            </div>
          </div>

          {/* SEO Recommendations */}
          <div className="seo-recommendations">
            <h3>SEO Recommendations</h3>
            <div className="recommendations-list">
              {stats.missingAltTags > 0 && (
                <div className="recommendation-item warning">
                  <i className="fas fa-exclamation-triangle"></i>
                  <div>
                    <strong>Add Alt Tags</strong>
                    <p>You have {stats.missingAltTags} images without alt text. Add descriptive alt tags to improve accessibility and SEO.</p>
                  </div>
                </div>
              )}
              
              {stats.missingDescriptions > 0 && (
                <div className="recommendation-item warning">
                  <i className="fas fa-file-text"></i>
                  <div>
                    <strong>Add Gallery Descriptions</strong>
                    <p>{stats.missingDescriptions} galleries are missing descriptions. Add detailed descriptions to improve search visibility.</p>
                  </div>
                </div>
              )}

              {stats.totalBlogPosts < 5 && (
                <div className="recommendation-item info">
                  <i className="fas fa-blog"></i>
                  <div>
                    <strong>Create More Blog Content</strong>
                    <p>You have {stats.totalBlogPosts} blog posts. Consider adding more content to improve your SEO ranking.</p>
                  </div>
                </div>
              )}

              {stats.seoScore >= 80 && (
                <div className="recommendation-item success">
                  <i className="fas fa-check-circle"></i>
                  <div>
                    <strong>Great Job!</strong>
                    <p>Your SEO score is excellent. Keep up the good work and continue monitoring your performance.</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="seo-quick-actions-section">
            <h3>Quick Actions</h3>
            <SEOQuickActions
              onAction={(action) => {
                console.log('Quick action:', action);
                if (action === 'run-health-check') {
                  setActiveTab('health');
                } else if (action === 'generate-sitemap') {
                  setActiveTab('sitemap');
                }
              }}
            />
          </div>
        </div>
      )}

      {activeTab === 'pages' && (
        <div className="seo-pages-tab">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
            <h3>Page SEO Management</h3>
            <HelpButton section="seo-pages" tooltip="Page SEO Management Help" />
          </div>
          <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
            Click on any page to view and edit its SEO settings. Status indicators show the completeness of SEO configuration.
          </p>
          <PageSEOEditor
            onPageSelect={(path) => {
              // Navigate to settings tab with page pre-selected
              setActiveTab('settings');
              // Could add logic to pre-select the page in settings form
            }}
          />
        </div>
      )}

      {activeTab === 'content' && (
        <div className="seo-content-tab">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
            <h3>Content SEO Management</h3>
            <HelpButton section="seo-content" tooltip="Content SEO Management Help" />
          </div>
          <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
            Manage SEO settings for galleries, blog posts, and services individually.
          </p>
          <BulkSEOTools
            onBulkAction={(action, data) => {
              console.log('Bulk action:', action, data);
            }}
          />
          <div style={{ marginTop: '2rem', padding: '1.5rem', background: '#f9fafb', borderRadius: '8px' }}>
            <h4>Individual Content SEO</h4>
            <p style={{ color: '#6b7280' }}>
              To edit SEO for individual galleries, blog posts, or services, use their respective management pages in the admin panel.
              Each content item can have its own SEO settings configured.
            </p>
          </div>
        </div>
      )}

      {activeTab === 'sitemap' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
            <h3>Sitemap Generator</h3>
            <HelpButton section="seo-sitemap" tooltip="Sitemap Management Help" />
          </div>
          <SitemapGenerator 
            galleries={galleries} 
            blogPosts={blogPosts} 
            baseUrl={baseUrl} 
          />
        </div>
      )}

      {activeTab === 'health' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
            <h3>SEO Health Check</h3>
            <HelpButton section="seo-health" tooltip="SEO Health Check Help" />
          </div>
          <SEOHealthChecker
            galleries={galleries}
            blogPosts={blogPosts}
            onFix={(issueId) => {
              console.log('Fixing issue:', issueId);
              // Trigger refresh
              if (onRefresh) onRefresh();
            }}
          />
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="seo-dashboard">
          <div className="seo-overview">
            <div className="add-backlink-form">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                <h4>Global SEO Defaults</h4>
                <HelpButton section="seo-settings" tooltip="SEO Settings Help" />
              </div>
              <SEOSettingsForm />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SEODashboard;
